import type { ReferentialAction } from '#layers/data-model/constants/schema/schema';
import type { PrimitiveType } from '#layers/data-model/types/schema/primitive-type';
import * as z from 'zod/v4-mini';

/**
 * Frontend contract for the engine's `CanonicalSchema` wire shape returned by
 * `POST /{workspace}/sources/data/introspect` (backend `crates/data-types/src/schema.rs`,
 * serialized camelCase). The Zod schemas below are the single source of truth: every `Canonical*`
 * type is derived from them with `z.infer`, so the runtime guard and the compile-time types cannot
 * drift. The engine structs are `#[serde(deny_unknown_fields)]`, so the payload carries exactly
 * these keys and `parseIntrospectDataSourceResponse` returns Zod's parsed output without loss.
 *
 * The shared `PrimitiveType` and `ReferentialAction` types are reused via `z.custom`: both carry
 * compile-time-only detail (a phantom value type on `PrimitiveType`, a const string-union on
 * `ReferentialAction`) that a structural schema cannot mirror. `kind` and `defaultValue` stay
 * permissive (`string` / `unknown`) so the guard validates the load-bearing structure rather than
 * those vocabularies.
 */

const canonicalIndexSchema = z.object({
	name: z.string(),
	fields: z.array(z.string()),
	kind: z.string(),
});

const canonicalFieldSchema = z.object({
	name: z.string(),
	apiName: z.optional(z.string()),
	// The shared `PrimitiveType` union carries a phantom value type, so it is guarded with `z.custom`
	// (as in `constants/validation/schema.ts`). The `'name' in value` check keeps the guard no weaker
	// than the previous `z.object({ name: z.string() })` while preserving `type.params` for the adapter.
	type: z.custom<PrimitiveType>((value) => typeof value === 'object' && value !== null && 'name' in value),
	isNullable: z.boolean(),
	isList: z.boolean(),
	defaultValue: z.optional(z.unknown()),
	isReadonly: z.boolean(),
});

export type CanonicalField = z.infer<typeof canonicalFieldSchema>;

const canonicalCollectionSchema = z.object({
	name: z.string(),
	apiName: z.optional(z.string()),
	fields: z.array(canonicalFieldSchema),
	indexes: z.array(canonicalIndexSchema),
	isReadonly: z.boolean(),
});

export type CanonicalCollection = z.infer<typeof canonicalCollectionSchema>;

const canonicalNamespaceSchema = z.object({
	name: z.string(),
	collections: z.array(canonicalCollectionSchema),
});

const canonicalRelationSchema = z.object({
	name: z.string(),
	constrainedNamespace: z.optional(z.string()),
	constrainedCollection: z.string(),
	constrainedFields: z.array(z.string()),
	constrainedRelationFieldName: z.optional(z.string()),
	referencedNamespace: z.optional(z.string()),
	referencedCollection: z.string(),
	referencedFields: z.array(z.string()),
	referencedRelationFieldName: z.optional(z.string()),
	onDelete: z.custom<ReferentialAction>((value) => typeof value === 'string'),
	onUpdate: z.custom<ReferentialAction>((value) => typeof value === 'string'),
});

export type CanonicalRelation = z.infer<typeof canonicalRelationSchema>;

const canonicalSchemaSchema = z.object({
	/** Namespaced collections (schemas/databases). */
	namespaces: z.array(canonicalNamespaceSchema),
	/** Top-level, namespace-less collections. */
	collections: z.array(canonicalCollectionSchema),
	relations: z.array(canonicalRelationSchema),
});

export type CanonicalSchema = z.infer<typeof canonicalSchemaSchema>;

const introspectDataSchema = z.object({
	schema: canonicalSchemaSchema,
	hash: z.string(),
});

/** Payload of the introspect endpoint: the discovered schema plus its blake3 hash. */
export type IntrospectDataSourceResponse = z.infer<typeof introspectDataSchema>;

/** Envelope wrapping the introspect payload under a `data` key. */
const introspectResponseSchema = z.object({
	data: introspectDataSchema,
});

/**
 * Target shape submitted to the (not-yet-merged) create-with-exclusions endpoint. The picker
 * derives this from the user's opt-in selection: everything unselected becomes excluded. The
 * `hash` guards against schema drift between introspection and import. This is an outbound shape
 * built by the frontend, not parsed from the wire, so it stays a plain interface.
 */
export interface ExcludedNamespace {
	dbName: string;
}

export interface ExcludedCollection {
	/** Absent for top-level, namespace-less collections. */
	namespaceDbName?: string;
	dbName: string;
}

export interface IntrospectionSelection {
	hash: string;
	excludedNamespaces: ExcludedNamespace[];
	excludedCollections: ExcludedCollection[];
}

/**
 * Exclusion list carried by the re-introspection apply body
 * (`POST /{workspace}/schema/introspect/{source}`, backend `IntrospectionExclusions`) and stored on
 * the data source's `introspectionExclusions` field. Same excluded shape as `IntrospectionSelection`
 * but WITHOUT `hash`: the re-scan apply forwards the diff itself, so no drift hash is needed.
 */
export interface IntrospectionExclusions {
	excludedNamespaces: ExcludedNamespace[];
	excludedCollections: ExcludedCollection[];
}

/**
 * Validate the introspect response envelope and return its `data` payload. Throws when the payload
 * does not match the expected shape. Because every engine struct is `#[serde(deny_unknown_fields)]`,
 * the wire payload carries exactly the declared keys, so returning Zod's parsed output is lossless.
 */
export function parseIntrospectDataSourceResponse(payload: unknown): IntrospectDataSourceResponse {
	const result = introspectResponseSchema.safeParse(payload);

	if (!result.success) {
		throw new Error(`Invalid introspection response: ${JSON.stringify(result.error.issues)}`);
	}

	return result.data.data;
}

const excludedNamespaceSchema = z.object({ dbName: z.string() });
const excludedCollectionSchema = z.object({
	// The engine serializes a namespace-less collection's `namespaceDbName` as `null` (its Rust
	// `Option<String>` carries no `skip_serializing_if`), while the create flow omits the key. Accept
	// both so a stored top-level exclusion parses instead of failing; `toExcludedCollection`
	// normalizes it back to an omitted key.
	namespaceDbName: z.nullish(z.string()),
	dbName: z.string(),
});

/**
 * Parse each element of `value` against `schema`, keeping the matches and dropping the rest. A single
 * malformed row can no longer discard the whole list, and a non-array payload degrades to empty.
 */
function parseStoredExclusionRows<Schema extends z.ZodMiniType>(schema: Schema, value: unknown): z.infer<Schema>[] {
	if (!Array.isArray(value)) {
		return [];
	}

	const rows: z.infer<Schema>[] = [];

	for (const item of value) {
		const result = schema.safeParse(item);

		if (result.success) {
			rows.push(result.data);
		}
	}

	return rows;
}

/**
 * Normalize a collection identity into an `ExcludedCollection`, dropping the key entirely for a
 * namespace-less collection (whether its `namespaceDbName` is omitted or serialized as `null`).
 * Shared by `collapseExclusions` and stored-exclusion parsing so both emit the same wire shape.
 */
export function toExcludedCollection(collection: { namespaceDbName?: string | null; dbName: string }): ExcludedCollection {
	if (collection.namespaceDbName === undefined || collection.namespaceDbName === null) {
		return { dbName: collection.dbName };
	}

	return { namespaceDbName: collection.namespaceDbName, dbName: collection.dbName };
}

/**
 * Read the data source's stored `introspectionExclusions` field into a well-formed
 * `IntrospectionExclusions`. Nothing-excluded is stored as `null`, so a `null` or non-object payload
 * degrades to empty lists. Within a valid payload each row is parsed on its own: a malformed entry is
 * dropped without discarding the rest, and a namespace-less collection whose `namespaceDbName` is
 * omitted or `null` is normalized back to an omitted key.
 */
export function parseStoredIntrospectionExclusions(payload: unknown): IntrospectionExclusions {
	const root = payload as { excludedNamespaces?: unknown; excludedCollections?: unknown } | null;

	return {
		excludedNamespaces: parseStoredExclusionRows(excludedNamespaceSchema, root?.excludedNamespaces),
		excludedCollections: parseStoredExclusionRows(excludedCollectionSchema, root?.excludedCollections)
			.map((collection) => toExcludedCollection(collection)),
	};
}
